import {useEffect, useState} from 'react';
import {
  bulkPutSites, clearSites,
  getAllSites,
  getLastUpdated,
  getSchemaVersion,
  setLastUpdated,
  setSchemaVersion
} from '@/utils/db/sitesDb';
import useCoreSiteList from "@/hooks/useCoreSiteList";
import _ from "lodash";

/**
 * This hook synchronizes "site" data (typically markers or physical locations) from a remote API
 * into a local IndexedDB cache. This allows for fast offline access and efficient spatial
 * filtering on the client side without refetching thousands of records.
 */

// Keep track of which page numbers we've already written to IndexedDB during this session.
// We keep this outside the hook so it persists even if the user navigates away and back.
const writtenPages = new Set();
const dbPromises = []

export function useSyncSitesToIndexedDb() {
  const sessionLastUpdated = typeof window === 'undefined' ? null : window.sessionStorage.getItem('lastUpdated');
  const [internalLastUpdated, setInternalLastUpdated] = useState(sessionLastUpdated ? sessionLastUpdated : null);
  const [internalSchemaVersion, setInternalSchemaVersion] = useState(null);
  const [apiSchemaVersion, setApiSchemaVersion] = useState(null);
  const [overallTotal, setOverallTotal] = useState(0);
  const [updatesTotal, setUpdatesTotal] = useState(0);
  const [updatesLoaded, setUpdatesLoaded] = useState(0);
  const [allSites, setAllSites] = useState([]);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [updatesAvailable, setUpdatesAvailable] = useState(false);

  // Fetch incremental updates from the API based on the lastUpdated timestamp
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    error,
    isLoading
  } = useCoreSiteList({lastUpdated: internalLastUpdated, enabled: internalLastUpdated !== null});

  // On mount, check IndexedDB for the last successful sync timestamp and schema version
  useEffect(() => {
    Promise.all([getLastUpdated(), getSchemaVersion()]).then(([lastUpdatedVal, schemaVersionVal]) => {
      setInternalLastUpdated(lastUpdatedVal || undefined);
      setInternalSchemaVersion(schemaVersionVal || undefined);
    });
  }, []);

  // On mount Load all existing sites from local storage into memory
  useEffect(() => {
    getAllSites().then((sites) => {
      setAllSites(sites);
      setTotalLoaded(sites.length);
    });
  }, [internalLastUpdated, internalSchemaVersion]);

  useEffect(() => {
    if (internalSchemaVersion === null) return;
    if (apiSchemaVersion === null) return;
    if (internalSchemaVersion === apiSchemaVersion) return;
    // todo
    console.log(`Schema version mismatch detected. Clearing sites from IndexedDB.`)
    clearSites()
      .then(() => {
        setAllSites([]);
        setTotalLoaded(0);
        return setLastUpdated(undefined)
      })
      .then(() => setInternalLastUpdated(undefined));
  }, [internalSchemaVersion, apiSchemaVersion]);

  useEffect(() => {
    writtenPages.clear()
    dbPromises.length = 0
  }, [internalLastUpdated, internalSchemaVersion]);

  // Get the updated sites from the API
  useEffect(() => {
    const lastPage = data?.pages?.[data.pages.length - 1];
    if (!lastPage || lastPage.page === undefined) return;

    const {total, overallTotal, lastUpdated = null, schemaVersion = null} = lastPage;
    setApiSchemaVersion(schemaVersion);
    const {page, pageSize } = lastPage;
    console.log(`got meta`, {page, pageSize, total, overallTotal, lastUpdated, schemaVersion, hasNextPage, lastPageCount: lastPage.data?.length})

    // write any new data to the db
    data?.pages?.forEach(page => {
      if (!page?.page) return
      if (writtenPages.has(page.page)) return;

      const partialSites = page?.data?.map((site) => ({
        ...site,
        // Ensures coordinates are numbers for Google Maps compatibility
        latitude: Number(site.latitude),
        longitude: Number(site.longitude),
      }))
      writtenPages.add(page.page)
      if (partialSites.length > 0) dbPromises.push(bulkPutSites(partialSites));
    });

    // count updates sites that have been loaded
    const sitesLoaded = data?.pages.reduce((count, page) => {
      return count + page?.data?.length || 0;
    }, 0);

    setOverallTotal(overallTotal)
    setUpdatesTotal(total)
    if (hasNextPage) {
      setUpdatesAvailable(true)
      setUpdatesLoaded(sitesLoaded);

      // yield to the UI (helps avoid jank with big data)
      setTimeout(() => fetchNextPage(), 250)
    } else {
      // after the last page, resolve the page writes and then update the db metadata
      Promise.all(dbPromises).then(async () => {
        // const allSites = await getAllSites();
        // setAllSites(allSites);
        // setTotalLoaded(allSites.length);
        setUpdatesLoaded(sitesLoaded);

        console.log('last page metadata', {lastUpdated, schemaVersion})
        if (typeof window !== 'undefined') window.sessionStorage.setItem('lastUpdated', lastUpdated);
        setLastUpdated(lastUpdated).catch(e => console.error('Error saving last updated timestamp:', e));
        setSchemaVersion(schemaVersion).catch(e => console.error('Error saving schema version:', e));

        setUpdatesAvailable(false)
      });
    }
  }, [data]);

  /**
   * Filters the cached sites based on a Google Maps LatLngBounds object.
   * This is used to only render markers that are currently visible in the viewport.
   */
  const filteredSites = (bounds) => {
    if (!bounds || !bounds.ne || !bounds.sw) return [];

    const {ne, sw} = bounds;
    // Extract coordinates from Google Maps LatLng objects
    if (!ne.lat || !ne.lng || !sw.lat || !sw.lng) return [];

    const maxLat = Math.max(ne.lat(), sw.lat())
    const maxLng = Math.max(ne.lng(), sw.lng())
    const minLat = Math.min(ne.lat(), sw.lat())
    const minLng = Math.min(ne.lng(), sw.lng())

    return allSites.filter((site) => {
      const lat = site.latitude;
      const lng = site.longitude;

      // Validate coordinates to ensure they are within standard Earth bounds
      if (
        !Number.isFinite(lat) ||
        !Number.isFinite(lng) ||
        lat < -90 || lat > 90 ||
        lng < -180 || lng > 180
      ) {
        return false;
      }
      // Filter out null island (0,0) or invalid data
      if (lat === 0 && lng === 0) return false;

      // Check if the site is within the bounding box provided by the map

      return (lat >= minLat && lat <= maxLat) && (lng >= minLng && lng <= maxLng);
    });
  }

  return {
    isError,
    error,
    overallTotal,
    updatesTotal,
    updatesLoaded,
    totalLoaded,
    filteredSites,
    updatesAvailable,
    isLoading,

    // currently unused but may be useful for debugging
    allSites,
  };
}
